import React, { useMemo, useEffect, useState } from 'react';

const ScrollingText = ({
  sentences = ["This is a scrolling text example"],
  speed = 50,
  className = "",
  separator = " • ",
  backgroundColor = "#1e3a8a",
  textColor = "white",
  fontFamily = "Arial, sans-serif",
  fontSize = 16,
  noScroll = false
}) => {
  const textArray = Array.isArray(sentences) ? sentences : [sentences];
  const combinedText = textArray.join(separator);

  // Track actual screen width
  const [screenWidth, setScreenWidth] = useState<number>(0);

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;

      setScreenWidth(screenWidth > 0 ? screenWidth : 1920);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []); // trigger if the window is resized

  // Calculate animation duration based on text length and speed (pixels per second)
  const animationDuration = useMemo(() => {
    if (noScroll) return 0;
    
    // Estimate pixel width: average character width is approximately 0.6 * fontSize
    const avgCharWidth = fontSize * 0.6;
    const estimatedTextWidth = combinedText.length * avgCharWidth;
    
    // Duration = (text width + screen width) / speed
    const totalDistance = estimatedTextWidth + screenWidth;
    
    // speed is now "pixels per second"
    const duration = totalDistance / speed;
    
    return Math.max(duration, 1);
  }, [combinedText, speed, noScroll, fontSize, screenWidth]);

  const containerStyle: React.CSSProperties = {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    width: '100%',
    position: 'relative',
    background: backgroundColor,
    padding: '12px 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: noScroll ? 'center' : 'flex-start',
    height: '50px',
  };

  const textStyle: React.CSSProperties = {
    display: 'inline-block',
    animation: noScroll ? 'none' : `scroll ${animationDuration}s linear infinite`,
    color: textColor,
    fontSize: `${fontSize}px`,
    fontFamily: fontFamily,
    fontWeight: '500',
    paddingLeft: noScroll ? '0' : '100%',
    lineHeight: '1',
    margin: 0,
    whiteSpace: 'nowrap',
  };

  const keyframes = `
    @keyframes scroll {
      0% {
        transform: translateX(0);
      }
      100% {
        transform: translateX(-100%);
      }
    }
  `;

  return (
    <>
      {!noScroll && <style>{keyframes}</style>}
      <div style={containerStyle} className={className}>
        <div style={textStyle}>
          {combinedText}
        </div>
      </div>
    </>
  );
};

export default ScrollingText;